/** Workspace concepts (spec §7.5, §8.11): minimal create/edit for the prototype. */
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { LoroDoc } from "loro-crdt";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useT } from "../i18n";
import { createConcept, deleteConcept, readConcepts, updateConcept } from "./model";

export function ConceptsPanel({ wsHandle, workspaceId, readOnly }: { wsHandle: DocHandle | null; workspaceId: string; readOnly: boolean }) {
  const t = useT();
  const concepts =
    useLoroSelector(wsHandle?.doc ?? null, (d: LoroDoc) => readConcepts(d, workspaceId), (a, b) => JSON.stringify(a) === JSON.stringify(b)) ?? [];
  const [label, setLabel] = useState("");

  return (
    <section className="kss-surface p-4" data-testid="concepts-panel">
      <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{t("concepts.title")}</h2>
      {concepts.length === 0 && <p className="text-neutral-500">{t("concepts.empty")}</p>}
      <ul className="space-y-1">
        {concepts.map((c) => (
          <li key={c.id} className="flex items-center gap-1" data-testid="concept-item">
            <input
              className="grow bg-transparent outline-none"
              defaultValue={c.prefLabel}
              readOnly={readOnly}
              data-testid="concept-label"
              onBlur={(e) => wsHandle && e.target.value !== c.prefLabel && updateConcept(wsHandle.doc, c.id, { prefLabel: e.target.value })}
            />
            {!readOnly && (
              <button className="text-neutral-400 hover:text-red-700" title={t("concepts.delete")} onClick={() => wsHandle && deleteConcept(wsHandle.doc, c.id)}>
                <Trash2 size={13} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <form
          className="mt-2 flex gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (wsHandle && label.trim()) {
              createConcept(wsHandle.doc, label.trim());
              setLabel("");
            }
          }}
        >
          <Input placeholder={t("concepts.new")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-concept" />
          <Button type="submit" title={t("review.grounding.createConcept")}>
            <Plus size={14} />
          </Button>
        </form>
      )}
    </section>
  );
}
